import { useCallback, useEffect, useRef, useState } from "react";

import { useHistory } from "react-router-dom";

import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { BASKET_PAYEE_PATIENT } from "@/core/constants";
import { SAVE_SHIPPING_ADDRESS_AT_CHECKOUT_MUTATION } from "@/graphql/organisations/mutations";
import { NON_FIELD_ERROR_KEY } from "@/hooks/form/useDjangoGraphqlForm";
import useScrollToError from "@/hooks/form/useScrollToError";
import useCheckoutShippingCountries from "@/hooks/useCheckoutShippingCountries";
import { debounce, get } from "@/utils/nodash";
import { useMutation } from "@apollo/client";

import useCmpRelatedUrl from "./useCmpRelatedUrl";

const SHIPPING_ADDRESS_PREFIX = "shippingAddress";
// At the time of writing these fields are unique to the shipping address form
const SHIPPING_ADDRESS_FIELDS = [
  "id",
  "name",
  "line_1",
  "line_2",
  "city",
  "postcode",
  "country",
  "phone_number"
];

export default function useShippingAddressAndFinalForm({ formApi, submitMutation, mutation }) {
  const [scrollToError, setScrollToError] = useState(0);

  const { basket } = useOrganisationBasketContext();

  const shippingCountries = useCheckoutShippingCountries();

  const [shippingAddressMutation, { loading }] = useMutation(
    SAVE_SHIPPING_ADDRESS_AT_CHECKOUT_MUTATION
  );

  const shippingAddressId = formApi.watch("shippingAddress.id");

  const haveExistingShippingAddress = !!basket?.shippingAddress && !!shippingAddressId;
  const [editShippingAddress, setEditShippingAddress] = useState(false);

  const showButtonsForShippingAddressForm = haveExistingShippingAddress && editShippingAddress;
  const shippingAddressFormEditable =
    showButtonsForShippingAddressForm || !haveExistingShippingAddress;

  const shippingAddressSubmitButtonRef = useRef();
  const checkoutSubmitButtonRef = useRef();

  const history = useHistory();

  const {
    setError,
    watch,
    formState: { errors }
  } = formApi;

  const nonFieldError = get(errors, NON_FIELD_ERROR_KEY)?.message;
  const handleShippingAddressMutationRejection = useCallback(() => {
    formApi.setError(NON_FIELD_ERROR_KEY, {
      type: "custom",
      message: "Unable to save your shipping address. Please try again."
    });
    shippingAddressSubmitButtonRef.current?.setSuccessful(false);
    setScrollToError(v => v + 1);
  }, [formApi]);

  const saveShippingAddressAtCheckout = useCallback(
    formValues => {
      if (!formValues.shippingAddress?.country || !basket?.id) {
        return Promise.resolve();
      }

      formApi.clearErrors(NON_FIELD_ERROR_KEY);

      const input = {
        ...formValues.shippingAddress,
        line1: formValues.shippingAddress.line_1,
        line2: formValues.shippingAddress.line_2,
        phoneNumber: (formValues.shippingAddress.phone_number || "").replaceAll(" ", ""),
        organisation: formValues.organisation,
        basket: basket.id
      };

      delete input.line_1;
      delete input.line_2;
      delete input.phone_number;

      return shippingAddressMutation({
        variables: {
          input
        }
      })
        .then(resp => {
          const errors = resp.data.saveShippingAddressAtCheckoutMutation.errors;
          if (errors.length) {
            errors.forEach(error => {
              setError(
                error.field === "__all__"
                  ? NON_FIELD_ERROR_KEY
                  : `${SHIPPING_ADDRESS_PREFIX}.${error.field}`,
                {
                  type: "custom",
                  message: error.messages[0]
                }
              );
            });
            setScrollToError(v => v + 1);
          } else {
            // a successful mutation means the basket has a new shipping address
            // so the component using this hook should have a useEffect watching the basket
            // which will reset the form
            // Patient pay only needs the country saved upfront; keep the form read-only
            // until the practitioner clicks edit address.
            setEditShippingAddress(basket?.payee !== BASKET_PAYEE_PATIENT);
          }
        })
        .catch(handleShippingAddressMutationRejection);
    },
    [
      basket?.id,
      basket?.payee,
      formApi,
      handleShippingAddressMutationRejection,
      setEditShippingAddress,
      setError,
      shippingAddressMutation
    ]
  );

  useEffect(() => {
    const debouncedCallback = debounce(
      (data, { type }) => {
        // If the shipping address country changes and we don't have a id we need to create the shipping address
        // now, on the fly, and store against the basket, so that the locale prices update correctly.
        // removed check for basket.payee as we need to update regardless for shipping address.
        if (type === "change" && !data.shippingAddress.id) {
          saveShippingAddressAtCheckout(data);
        }
      },
      1000,
      {
        trailing: true
      }
    );

    const subscription = watch(debouncedCallback);
    return () => subscription.unsubscribe();
  }, [watch, saveShippingAddressAtCheckout]);

  const { resolvedUrls } = useCmpRelatedUrl();

  const onSubmit = useCallback(
    formValues => {
      if (haveExistingShippingAddress && editShippingAddress) {
        const input = {
          ...formValues.shippingAddress,
          line1: formValues.shippingAddress.line_1,
          line2: formValues.shippingAddress.line_2,
          phoneNumber: (formValues.shippingAddress.phone_number || "").replaceAll(" ", ""),
          organisation: formValues.organisation,
          basket: basket.id
        };

        delete input.line_1;
        delete input.line_2;
        delete input.phone_number;

        return shippingAddressMutation({
          variables: {
            input
          }
        })
          .then(resp => {
            const errors = resp.data.saveShippingAddressAtCheckoutMutation.errors;
            if (errors.length) {
              errors.forEach(error => {
                formApi.setError(
                  error.field === "_All__"
                    ? NON_FIELD_ERROR_KEY
                    : `${SHIPPING_ADDRESS_PREFIX}.${error.field}`,
                  {
                    type: "custom",
                    message: error.messages[0]
                  }
                );
              });
              shippingAddressSubmitButtonRef.current?.setSuccessful(false);
              setScrollToError(v => v + 1);
            } else {
              shippingAddressSubmitButtonRef.current?.setSuccessful(true);
              formApi.reset({
                ...formValues
              });
              setEditShippingAddress(false);
            }
          })
          .catch(handleShippingAddressMutationRejection)
          .finally(() => {
            shippingAddressSubmitButtonRef.current?.setPending(false);
          });
      } else {
        const input = {
          ...formValues,
          shippingAddress: JSON.stringify({
            ...formValues[SHIPPING_ADDRESS_PREFIX],
            phone_number: (formValues[SHIPPING_ADDRESS_PREFIX].phone_number || "").replaceAll(
              " ",
              ""
            )
          })
        };

        delete input["acknowledge"]; // sometimes present, sometimes not

        submitMutation({
          variables: {
            input
          }
        })
          .then(resp => {
            const errors = resp.data[mutation.mutationName].errors;
            if (errors.length) {
              errors.forEach(error => {
                formApi.setError(
                  error.field === "_All__"
                    ? NON_FIELD_ERROR_KEY
                    : SHIPPING_ADDRESS_FIELDS.includes(error.field)
                    ? `${SHIPPING_ADDRESS_PREFIX}.${error.field}`
                    : error.field,
                  {
                    type: "custom",
                    message: error.messages[0]
                  }
                );
              });
              checkoutSubmitButtonRef.current?.setSuccessful(false);
              checkoutSubmitButtonRef.current?.setPending(false);
              setScrollToError(v => v + 1);
            } else {
              checkoutSubmitButtonRef.current?.setSuccessful(true);
              checkoutSubmitButtonRef.current?.setPending(false);
              mutation.handleSuccess?.({
                formResponseRelated: resp,
                history,
                setScrollToError,
                resolvedUrls
              });
            }
          })
          .catch(e => {
            console.log("Error checking out", e);
          });
      }
    },
    [
      setEditShippingAddress,
      shippingAddressMutation,
      haveExistingShippingAddress,
      editShippingAddress,
      handleShippingAddressMutationRejection,
      mutation,
      submitMutation,
      formApi,
      history,
      basket,
      setScrollToError,
      resolvedUrls
    ]
  );

  const onInvalid = useCallback(() => {
    shippingAddressSubmitButtonRef.current?.setSuccessful(false);
    shippingAddressSubmitButtonRef.current?.setPending(false);
    checkoutSubmitButtonRef.current?.setSuccessful(false);
    checkoutSubmitButtonRef.current?.setPending(false);
    setScrollToError(v => v + 1);
  }, []);

  const onShippingAddressSubmit = useCallback(
    async event => {
      event.preventDefault();
      formApi.clearErrors(NON_FIELD_ERROR_KEY);
      const valid = await formApi.trigger(
        SHIPPING_ADDRESS_FIELDS.map(field => `${SHIPPING_ADDRESS_PREFIX}.${field}`)
      );
      if (!valid) {
        onInvalid();
        return;
      }
      return onSubmit(formApi.getValues());
    },
    [formApi, onInvalid, onSubmit]
  );

  useScrollToError({
    scrollToError
  });

  return {
    haveExistingShippingAddress,
    editShippingAddress,
    setEditShippingAddress,
    showButtonsForShippingAddressForm,
    shippingAddressFormEditable,
    shippingAddressSubmitButtonRef,
    onSubmit,
    onInvalid,
    onShippingAddressSubmit,
    saveShippingAddressAtCheckout,
    shippingCountries,
    SHIPPING_ADDRESS_PREFIX,
    loading,
    nonFieldError
  };
}
